/**
 * CruxPopularityCard — the origin-level trend companion beside the Pages
 * panel. Finally surfaces what the BigQuery deep-dive uniquely pulls and
 * the rest of the page ignores: the multi-year monthly field-score trend
 * and the popularity rank over time (the free History API gives neither).
 *
 * Paid-only (rides the same `useSiteCruxHistory` the hero rank line
 * uses); renders null when unentitled or without monthly coverage, so
 * the Pages panel simply fills the row.
 */
import { Sparkline } from '@/components/primitives/Sparkline';
import { rankBand, rankMagnitude } from '@/lib/crux-geo-metric';
import { bandFill, bandText, scoreBand } from '@/lib/format';
import { useEntitlement } from '@/lib/queries/billing';
import { type CruxMonthlyRow, useSiteCruxHistory } from '@/lib/queries/crux';
import { cn } from '@/lib/utils';
import { TrendDown, TrendUp } from '@phosphor-icons/react';
import type { ReactElement } from 'react';

/** Same weighting the API's `fieldScore` uses, applied per month so we
 *  can chart the score over time (the history payload only carries the
 *  latest computed score). Renormalises over the metrics present. */
const SCORE_WEIGHTS: ReadonlyArray<[keyof CruxMonthlyRow, number]> = [
  ['lcp_good', 0.35],
  ['inp_good', 0.3],
  ['cls_good', 0.15],
  ['ttfb_good', 0.1],
  ['fcp_good', 0.1],
];

function monthScore(r: CruxMonthlyRow): number | null {
  let sum = 0;
  let weight = 0;
  for (const [key, w] of SCORE_WEIGHTS) {
    const good = r[key];
    if (typeof good === 'number') {
      sum += good * w;
      weight += w;
    }
  }
  return weight === 0 ? null : Math.round((sum / weight) * 100);
}

export function CruxPopularityCard({ siteUuid }: { siteUuid: string | null }): ReactElement | null {
  const { allowed } = useEntitlement('crux_geo');
  const history = useSiteCruxHistory(siteUuid, { enabled: allowed && !!siteUuid });

  const data = history.data;
  if (!allowed || !data || data.coverage !== 'ok' || data.series.length === 0) return null;
  const series = data.series;

  const scorePoints = series.map((r) => ({ x: Date.parse(r.yyyymm), y: monthScore(r) }));
  const latestScore = data.field_score ?? monthScore(series[series.length - 1]!);
  const band = scoreBand(latestScore);
  // ~12 months back (or the earliest we have) for the trend chip.
  const prior = series[Math.max(0, series.length - 13)];
  const priorScore = prior ? monthScore(prior) : null;
  const scoreDelta = latestScore != null && priorScore != null ? latestScore - priorScore : null;

  const rank = data.rank;
  const popBand = rankBand(rank);
  const popMag = rankMagnitude(rank);
  // Rank trend: lower bucket = more popular.
  const firstRank = series.find((r) => r.rank != null)?.rank ?? null;
  const popDir = rank != null && firstRank != null ? Math.sign(firstRank - rank) : 0;

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-[var(--color-ink)]">Trends</h3>
        <span className="text-[11px] text-[var(--color-mute)]">
          Chrome UX Report · {series.length} mo
        </span>
      </div>

      {/* Field score over time */}
      <div>
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] uppercase tracking-wide text-[var(--color-mute)]">
            Field score
          </span>
          {scoreDelta != null && scoreDelta !== 0 && <DeltaChip delta={scoreDelta} />}
        </div>
        <div className="mt-1 flex items-end gap-3">
          <span className={cn('text-2xl font-semibold leading-none tabular-nums', bandText(band))}>
            {latestScore ?? '—'}
          </span>
          <Sparkline
            data={scorePoints}
            width={200}
            height={36}
            className="h-9 w-full flex-1"
            stroke={bandFill(band)}
            fill={bandFill(band)}
            ariaLabel="Field score over time"
          />
        </div>
      </div>

      {/* Popularity */}
      {popBand && (
        <div className="border-t border-[var(--color-line-soft)] pt-3">
          <span className="text-[11px] uppercase tracking-wide text-[var(--color-mute)]">
            Popularity
          </span>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--color-ink)]">{popBand}</span>
            {popMag && <span className="text-xs text-[var(--color-mute)]">· {popMag}</span>}
            {popDir !== 0 && (
              <span
                className={cn(
                  'ml-auto inline-flex items-center gap-0.5 text-[11px] font-medium',
                  popDir > 0 ? 'text-[var(--color-success)]' : 'text-[var(--color-mute)]',
                )}
              >
                {popDir > 0 ? (
                  <TrendUp size={12} weight="bold" />
                ) : (
                  <TrendDown size={12} weight="bold" />
                )}
                {popDir > 0 ? 'more popular' : 'less popular'}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function DeltaChip({ delta }: { delta: number }): ReactElement {
  const up = delta > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
        up
          ? 'bg-[var(--color-success-soft)] text-[var(--color-success)]'
          : 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
      )}
      title="vs a year ago"
    >
      {up ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
      {up ? '+' : ''}
      {delta}
    </span>
  );
}
